import React from 'react';
import { Link } from 'react-router-dom';
import { GaugeIcon, FuelIcon, MapPinIcon, CalendarIcon, MessageCircleIcon, ArrowRightIcon } from 'lucide-react';
import type { Vehicle } from '../types/vehicle';
import { Badge } from './ui/Badge';
import { cardWhatsappMessage, formatKm, formatPrice, whatsappLink } from '../utils/vehicles';
import { onImageError } from '../utils/drive';
export function VehicleCard({
  vehicle
}: {
  vehicle: Vehicle;
}) {
  const title = `${vehicle.marca} ${vehicle.modelo}`;
  return <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-surface2/50 shadow-card backdrop-blur-sm transition-[border-color,transform,box-shadow] duration-200 ease-smooth hover:-translate-y-1 hover:border-neon/60 hover:shadow-[0_0_36px_-14px_rgba(31,123,255,0.95)]" data-id="element-600" data-mp="lkfjgh89iayb">
      <Link to={`/vehiculos/${vehicle.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-ink" data-id="element-601" data-mp="6soswy6r5n6x">
        <img src={vehicle.imagenPrincipal} alt={`${title} ${vehicle.version} ${vehicle.anio}`} loading="lazy" onError={onImageError} referrerPolicy="no-referrer" className="h-full w-full object-cover transition-transform duration-300 ease-smooth group-hover:scale-[1.03]" data-id="element-602" data-mp="3u8c7d9uc72ol" />
        <div className="absolute inset-x-0 top-0 flex flex-wrap gap-1.5 p-3" data-id="element-603" data-mp="cqszfp64is9r">
          {vehicle.destacado ? <Badge tone="neon" data-id="element-604" data-mp="3bajel0dfg94x">Destacado</Badge> : null}
          {vehicle.oportunidad ? <Badge tone="amber" data-id="element-605" data-mp="3g81fc3haji1u">Oportunidad</Badge> : null}
          {vehicle.financiacion ? <Badge tone="green" data-id="element-606" data-mp="3l7nskcx4cyan">Financiación</Badge> : null}
        </div>
        <span className="absolute bottom-3 left-3 rounded-full bg-ink/85 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur" data-id="element-607" data-mp="3l6cqqin8i0m6">
          {vehicle.estado === '0KM' ? '0KM' : 'Usado seleccionado'}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-5" data-id="element-608" data-mp="15fqfdlmv4czc">
        <h3 className="text-[17px] font-bold leading-snug text-white" data-id="element-609" data-mp="15imdb9ypn1ns">
          {title}
          <span className="block text-[13px] font-medium text-mist" data-id="element-610" data-mp="31htl82vbyode">
            {vehicle.version}
          </span>
        </h3>

        <p className="mt-3 text-[22px] font-extrabold tracking-tight text-white" data-id="element-611" data-mp="3akchu58iw4r7">
          {formatPrice(vehicle.precio, vehicle.moneda)}
        </p>

        <dl className="mt-4 grid grid-cols-2 gap-y-2 text-[13px] text-mist" data-id="element-612" data-mp="36k2c8ton7gcv">
          <div className="flex items-center gap-1.5" data-id="element-613" data-mp="2isdjxdbtmrvx">
            <CalendarIcon className="h-3.5 w-3.5 text-neon" aria-hidden="true" data-id="element-614" data-mp="2iqslvdqasqtb" />
            <dt className="sr-only" data-id="element-615" data-mp="2dvwrw81i3tdk">Año</dt>
            <dd data-id="element-616" data-mp="2rm09umtcl0x7">{vehicle.anio}</dd>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-617" data-mp="2rmruj2lepe8p">
            <FuelIcon className="h-3.5 w-3.5 text-neon" aria-hidden="true" data-id="element-618" data-mp="2mu8gk4it4h3d" />
            <dt className="sr-only" data-id="element-619" data-mp="1z5602zzwsuag">Combustible</dt>
            <dd className="truncate" data-id="element-620" data-mp="1z3uvixyn3iia">{vehicle.combustible}</dd>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-621" data-mp="1v4dqu6r1epe6">
            <GaugeIcon className="h-3.5 w-3.5 text-neon" aria-hidden="true" data-id="element-622" data-mp="1v1rk29bz0dx0" />
            <dt className="sr-only" data-id="element-623" data-mp="1kychlr2zbve3">Kilometraje</dt>
            <dd data-id="element-624" data-mp="1pqvwqshbeyl0">{formatKm(vehicle.kilometraje)}</dd>
          </div>
          <div className="col-span-2 flex items-center gap-1.5" data-id="element-625" data-mp="1ps704r6um4bl">
            <MapPinIcon className="h-3.5 w-3.5 text-neon" aria-hidden="true" data-id="element-626" data-mp="1b31236jqyk5a" />
            <dt className="sr-only" data-id="element-627" data-mp="1g1m3h9aqj9vb">Ubicación</dt>
            <dd className="truncate" data-id="element-628" data-mp="1fy80byu8ywmt">{vehicle.ubicacion}</dd>
          </div>
        </dl>

        <div className="mt-auto flex gap-2 pt-5" data-id="element-629" data-mp="23o2c0d2tyf5h">
          <Link to={`/vehiculos/${vehicle.slug}`} className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-xl border border-line bg-surface2/70 text-[13px] font-semibold text-white transition-colors duration-150 ease-smooth hover:border-neon/70" data-id="element-630" data-mp="28phx2550clnv">
            Ver detalle
            <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-631" data-mp="ib90jf89d8kp" />
          </Link>
          <a href={whatsappLink(cardWhatsappMessage(vehicle), vehicle.whatsapp)} target="_blank" rel="noopener noreferrer" aria-label={`Consultar por WhatsApp el ${title} ${vehicle.version}`} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#1fae5a] px-3.5 text-[13px] font-semibold text-white transition-colors duration-150 ease-smooth hover:bg-[#25c264]" data-id="element-632" data-mp="ncomd9i9fj45">
            <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-633" data-mp="1mhm5jac7exa8" />
            <span className="hidden sm:inline" data-id="element-634" data-mp="1mft88lnhdpew">WhatsApp</span>
          </a>
        </div>
      </div>
    </article>;
}